import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import {
  useGetVgmPrintContainersQuery,
  useLazyGetVgmPrintPreviewQuery,
  type VgmContainerRow,
} from '@/store/api/saleAllocationVgmPrintApi'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: chooser `frmCtrlSalesVGMPr_Choose` + reports `rpCtrlSalesVGM_NN` /
// `rpCtrlSalesVGMTotal` (VGM — Verified Gross Mass of container, MARDEP form per
// Văn bản số 2428/CHHVN-VTDVHH). Main-screen `btnPrintVGM_Click` seeds the tick
// list from the selected row's delivery_order then opens this chooser.
//
// Two stages in one dialog:
//   1. Chooser — tick the containers to print + pick the container size, mirror
//      `frmCtrlSalesVGMPr_Choose` (subform tick list + container_type combo +
//      tick_all + btnPrint/btnCancel).
//   2. Preview — iframe srcDoc of the BE-rendered report HTML + Print button,
//      mirror `DoPrintDialog`.

interface SalesAllocationVgmPrintDialogProps {
  open: boolean
  /** Selected row's delivery_order (Access `do_no`). Empty → guarded upstream. */
  doNo: string
  canEdit?: boolean
  onClose: () => void
}

// Access `container_type` combo — Value List `"20 '";"40 '";"other"`.
const CONTAINER_TYPE_OPTIONS = ["20 '", "40 '", 'other'] as const

// Override CSS preview để fit A4 trong iframe scaled (memory: print fit A4).
// VGM report rộng (~267mm > A4 portrait) → LANDSCAPE (A4 ngang 297mm). Preview màn
// hình scale 0.8 cho vừa dialog; print full-size landscape (convert-pr: 2 dạng
// landscape/portrait theo orientation Access PrtDevMode).
const PRINT_PREVIEW_CSS = `
@page { size: A4 landscape !important; margin: 0 !important; }
@media print {
  @page { size: A4 landscape !important; margin: 0 !important; }
  body { margin: 0 !important; }
  .report-page { width: 297mm !important; max-width: none !important; min-height: 210mm !important; padding: 9mm 11mm !important; zoom: 1 !important; }
}
@media screen and (max-width: 600px) {
  .report-page { min-height: auto !important; max-width: 100% !important; width: 100% !important; padding: 4mm !important; margin: 0 !important; }
  body { margin: 0 !important; background: #fff !important; }
}
body { margin: 0 !important; }
.print-action { display: none !important; }
.report-page { width: 100% !important; max-width: 297mm !important; padding: 0 !important; min-height: auto !important; margin: 0 auto !important; zoom: 0.8 !important; }
.access-report { position: relative !important; margin: 0 auto !important; background: #fff !important; }
.ctl { position: absolute !important; overflow: visible !important; line-height: 1.2 !important; }
.ctl.bold { font-weight: 700 !important; }
.ctl.align-center { text-align: center !important; }
.ctl.align-right { text-align: right !important; }
.ctl img { width: 100% !important; height: 100% !important; object-fit: contain !important; object-position: left center !important; display: block !important; }
`.trim()

/** Inject base href + PRINT_PREVIEW_CSS override + strip inline print button. */
function buildVgmPreviewHtml(rawHtml: string): string {
  const withoutInlinePrint = rawHtml.replace(/<button\s+class="print-action[^>]*>[\s\S]*?<\/button>/g, '')
  const withBase = withoutInlinePrint.replace('<head>', `<head><base href="${window.location.origin}/">`)
  const styleTag = `<style id="vgm-print-preview-overrides">${PRINT_PREVIEW_CSS}</style>`
  if (withBase.includes('</head>')) return withBase.replace('</head>', `${styleTag}</head>`)
  return `${styleTag}${withBase}`
}

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

function formatKg(value: number | null): string {
  if (value === null || value === undefined) return ''
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export default function SalesAllocationVgmPrintDialog({
  open,
  doNo,
  canEdit = true,
  onClose,
}: SalesAllocationVgmPrintDialogProps) {
  // ── Chooser state ──────────────────────────────────────────────────────────
  const [checked, setChecked] = useState<Record<number, boolean>>({})
  const [containerType, setContainerType] = useState<string>(CONTAINER_TYPE_OPTIONS[0])
  // ── Preview state (mirror DoPrintDialog {html, loading}) ─────────────────────
  const [previewHtml, setPreviewHtml] = useState('')
  const [previewTitle, setPreviewTitle] = useState('')
  const [toastMessage, setToastMessage] = useState('')

  const {
    data: containerData,
    isFetching: containersLoading,
    isError: containersError,
  } = useGetVgmPrintContainersQuery(doNo, { skip: !open || !doNo })

  const [fetchPreview, { isFetching: previewLoading }] = useLazyGetVgmPrintPreviewQuery()

  const rows: VgmContainerRow[] = useMemo(() => containerData?.items ?? [], [containerData])

  // Access seeds the tick list with checked_row = 1 (all ticked by default).
  useEffect(() => {
    if (!open) return
    setChecked(Object.fromEntries(rows.map((r) => [r.salesAllocationScsId, true])))
  }, [open, rows])

  // Reset everything when the dialog closes.
  useEffect(() => {
    if (open) return
    setChecked({})
    setContainerType(CONTAINER_TYPE_OPTIONS[0])
    setPreviewHtml('')
    setPreviewTitle('')
  }, [open])

  const checkedRows = useMemo(
    () => rows.filter((r) => checked[r.salesAllocationScsId]),
    [rows, checked],
  )
  const allChecked = rows.length > 0 && checkedRows.length === rows.length
  const someChecked = checkedRows.length > 0 && !allChecked

  const handleTickAll = (next: boolean) => {
    setChecked(Object.fromEntries(rows.map((r) => [r.salesAllocationScsId, next])))
  }
  const handleTickOne = (id: number, next: boolean) => {
    setChecked((prev) => ({ ...prev, [id]: next }))
  }

  // btnPrint_Click — require ticked rows ("Please choose records to print!!"),
  // build the preview, switch to the preview stage.
  const handlePrint = async () => {
    if (checkedRows.length === 0) {
      setToastMessage('Please choose records to print!!')
      return
    }
    try {
      const res = await fetchPreview({
        doNo,
        containerNos: checkedRows.map((r) => r.containerNo),
        containerType,
      }).unwrap()
      if (!res.html) {
        setToastMessage(res.message || 'No data to print!')
        return
      }
      setPreviewTitle(res.title || doNo)
      setPreviewHtml(buildVgmPreviewHtml(res.html))
      if (res.message) setToastMessage(res.message)
    } catch {
      setToastMessage('Unable to build the print preview. Please try again.')
    }
  }

  const handleBackToChooser = () => {
    setPreviewHtml('')
    setPreviewTitle('')
  }

  const inPreview = Boolean(previewHtml)
  const iframeTitle = 'VGM Print Preview'

  // ── Preview stage (mirror DoPrintDialog) ─────────────────────────────────────
  if (inPreview) {
    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth={false}
          fullWidth
          PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
            <Box
              component="span"
              title={previewTitle}
              sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
              VGM ({previewTitle})
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={handleBackToChooser} size="small">
                Back
              </Button>
              {canEdit && (
                <Button
                  variant="contained"
                  size="small"
                  disabled={!previewHtml}
                  onClick={() => {
                    const printTitle = `${sanitizePdfFileNamePart(previewTitle || 'vgm')}_VGM.pdf`
                    void printAndUploadReport({
                      iframeTitle,
                      reportName: 'rpCtrlSalesVGM',
                      printTitle,
                      html: previewHtml,
                      onUploadError: setToastMessage,
                    })
                  }}
                >
                  Print
                </Button>
              )}
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
            <Box
              component="iframe"
              title={iframeTitle}
              srcDoc={previewHtml}
              sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
            />
          </DialogContent>
        </Dialog>
        <Snackbar
          open={Boolean(toastMessage)}
          message={toastMessage}
          autoHideDuration={2500}
          onClose={() => setToastMessage('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        />
      </>
    )
  }

  // ── Chooser stage (mirror frmCtrlSalesVGMPr_Choose) ──────────────────────────
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>
          Sales Control Sheet - Print VGM
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <Autocomplete
              size="small"
              options={[...CONTAINER_TYPE_OPTIONS]}
              value={containerType}
              onChange={(_e, v) => setContainerType(v ?? CONTAINER_TYPE_OPTIONS[0])}
              disableClearable
              getOptionLabel={(o) => o}
              isOptionEqualToValue={(a, b) => a === b}
              renderInput={(params) => <TextField {...params} label="Size Of containers" />}
            />

            {containersError && (
              <Alert severity="error">Unable to load containers for this delivery order.</Alert>
            )}

            {containersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : rows.length === 0 ? (
              <Alert severity="info">No containers available for this delivery order.</Alert>
            ) : (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allChecked}
                      indeterminate={someChecked}
                      onChange={(e) => handleTickAll(e.target.checked)}
                    />
                  }
                  label="Select all"
                />
                <TableContainer sx={{ maxHeight: '50vh' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>Container No.</TableCell>
                        <TableCell align="right">Max gross weight (kg)</TableCell>
                        <TableCell align="right">VGM (kg)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow key={r.salesAllocationScsId} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={Boolean(checked[r.salesAllocationScsId])}
                              onChange={(e) => handleTickOne(r.salesAllocationScsId, e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>{r.containerNo}</TableCell>
                          <TableCell align="right">{formatKg(r.maxGrossWeight)}</TableCell>
                          <TableCell align="right">{formatKg(r.vgm)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} size="small">
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={previewLoading || checkedRows.length === 0}
            onClick={() => void handlePrint()}
            startIcon={previewLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  )
}
